// ATLAS-5882: guard for the table that the email save wraps around a centred image's content.
//
// `WysiwygBase64Sanitizer.cleanExportedImageStyles(html, true)` puts a centred figure's img and figcaption inside
// `figure > table[role=presentation] > tbody > tr > td`, because Outlook desktop ignores margin:auto. CKEditor 5 48.1.1
// `upcastImageFigure` still finds the img through the wrapper (`findViewImgElement` walks descendants,
// @ckeditor/ckeditor5-image dist/index.js:341-350), but then converts the wrapper table as a child of the image
// (line 1064), and `matchImageCaptionViewElement` accepts only a figcaption whose parent is the `figure.image`
// (line 2622). Without this guard the caption is lost when saved email HTML is reopened.
//
// This guard hooks the (decorated) DataController `toModel` event at highest priority and, before any upcast runs,
// replaces each such wrapper table with the cell's children. The wrapper's table and cell attributes are dropped: they
// are email-only and the next email save writes them again.
//
// No SYNC copy in the Portal: only the Backoffice email save path writes the wrapper.
//
// REMOVAL CONDITION: delete this file and its ckeditor-config.ts registration only when both are true: the email
// cleanup no longer writes the wrapper, and no stored content still carries it (until a data repair is done, never).
// If a future CKEditor upcasts the wrapper without losing the caption, the guard is still needed for the model shape.
// The version test (ckeditor-email-image-wrapper-guard.spec.ts, G1) fails on any ckeditor5 bump to force a recheck that
// `upcastImageFigure` and `matchImageCaptionViewElement` still behave as described.
//
// No runtime imports: ViewUpcastWriter comes from the runtime object passed to the factory (cloud.CKEditor in production).
import type {
  PluginConstructor,
  ViewDocumentFragment,
  ViewElement,
  ViewNode,
  ViewUpcastWriter,
} from 'https://cdn.ckeditor.com/typings/ckeditor5.d.ts';

/** CKEditor version whose image upcast this guard compensates for. */
export const EMAIL_IMAGE_WRAPPER_GUARD_CKEDITOR_VERSION = '48.1.1';
export const EMAIL_IMAGE_WRAPPER_GUARD_PLUGIN_NAME = 'EmailImageWrapperGuard';

/** The only runtime class the guard needs, supplied from cloud.CKEditor (or, in tests, the npm ckeditor5 namespace). */
export interface EmailImageWrapperGuardRuntime {
  ViewUpcastWriter: typeof ViewUpcastWriter;
}

// ASCII whitespace only. Not \s: that matches U+00A0, and &nbsp; must count as content.
const ASCII_WHITESPACE_ONLY = /^[ \t\n\f\r]*$/;

function isContentChild(child: ViewNode): boolean {
  if (child.is('$text')) {
    return !ASCII_WHITESPACE_ONLY.test((child as any).data);
  }
  if (child.is('uiElement') || child.is('element', '$comment')) {
    return false;
  }
  return true;
}

function contentChildren(element: ViewElement): ViewNode[] {
  return Array.from(element.getChildren()).filter(isContentChild);
}

/** The single cell of a table with exactly one row and one cell, with or without a tbody; otherwise null. */
function getSoleCell(table: ViewElement): ViewElement | null {
  let container = table;
  let children = contentChildren(container);
  if (children.length === 1 && children[0].is('element', 'tbody')) {
    container = children[0] as ViewElement;
    children = contentChildren(container);
  }
  if (children.length !== 1 || !children[0].is('element', 'tr')) return null;
  const cells = contentChildren(children[0] as ViewElement);
  if (cells.length !== 1 || !(cells[0].is('element', 'td') || cells[0].is('element', 'th'))) return null;
  return cells[0] as ViewElement;
}

function containsImg(element: ViewElement): boolean {
  return Array.from(element.getChildren()).some(c => c.is('element', 'img') || (c.is('element') && containsImg(c as ViewElement)));
}

/** True for the table the email save puts around a figure.image's content (R1 centred shape, or the block-align shape). */
export function isEmailImageWrapper(node: ViewNode | ViewDocumentFragment): boolean {
  if (!node.is('element', 'table') || (node as ViewElement).getAttribute('role') !== 'presentation') return false;
  const figure = (node as ViewElement).parent;
  if (!figure || !figure.is('element', 'figure') || !(figure as ViewElement).hasClass('image')) return false;
  const siblings = contentChildren(figure as ViewElement);
  if (siblings.length !== 1 || siblings[0] !== node) return false;
  const cell = getSoleCell(node as ViewElement);
  return !!cell && containsImg(cell);
}

/** Unwraps every matching wrapper inside `root` (and `root` itself); returns the number of wrappers removed. */
export function unwrapEmailImageWrappers(root: ViewDocumentFragment | ViewElement, writer: ViewUpcastWriter): number {
  const targets: ViewElement[] = [];
  if (isEmailImageWrapper(root)) {
    targets.push(root as ViewElement);
  }
  for (const { item } of writer.createRangeIn(root)) {
    if (isEmailImageWrapper(item as ViewNode)) {
      targets.push(item as ViewElement);
    }
  }

  for (const table of targets) {
    const figure = table.parent as ViewElement;
    const cell = getSoleCell(table)!;
    const children = Array.from(cell.getChildren());
    const index = figure.getChildIndex(table);
    writer.remove(table);
    writer.insertChild(index, children, figure);
  }
  return targets.length;
}

/** Returns a function-style plugin (a `function`, not an arrow: CKEditor `new`s plugin constructors). */
export function createEmailImageWrapperGuard(ck: EmailImageWrapperGuardRuntime): PluginConstructor {
  function EmailImageWrapperGuard(this: any, editor: any) {
    this.editor = editor;
  }
  EmailImageWrapperGuard.pluginName = EMAIL_IMAGE_WRAPPER_GUARD_PLUGIN_NAME;
  EmailImageWrapperGuard.prototype.init = function (this: any) {
    const editor = this.editor;
    editor.data.on(
      'toModel',
      (_evt: unknown, [viewItem]: [ViewDocumentFragment | ViewElement]) => {
        unwrapEmailImageWrappers(viewItem, new ck.ViewUpcastWriter(editor.data.viewDocument));
      },
      { priority: 'highest' }
    );
  };
  EmailImageWrapperGuard.prototype.destroy = function () {};
  return EmailImageWrapperGuard as unknown as PluginConstructor;
}
